import { useEffect, useState } from "react";
import { Globe, Mail, MapPin, Phone, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Logo } from "../brand/Logo";

const DOC_TABS = [["capability", "Capability statement"], ["quotation", "Quotation"], ["proposal", "Proposal"],
  ["correspondence", "Letter"], ["procurement", "Procurement readiness"], ["submission", "Tender cover"]];

// "www.techcare.live" with the orange ".live" accent used on the official letterhead.
const Domain = ({ text }) => {
  const i = text.lastIndexOf(".");
  return <>{text.slice(0, i + 1)}<span className="text-orange-500">{text.slice(i + 1)}</span></>;
};

const Pill = ({ icon: Icon, children, testId }) => (
  <span className="inline-flex items-center gap-3 rounded-full border-2 border-orange-500 py-1.5 pl-1.5 pr-5 print:w-full print:gap-2 print:py-[3pt] print:pl-[3pt] print:pr-[10pt]" data-testid={testId}>
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-orange-500 print:h-[16pt] print:w-[16pt]"><Icon className="h-5 w-5 print:h-[11pt] print:w-[11pt]" strokeWidth={2.2} /></span>
    <span className="whitespace-nowrap text-[13px] font-bold tracking-[0.12em] text-stone-950 sm:text-sm print:text-[9pt]">{children}</span>
  </span>
);

const ICON = { website: Globe, email: Mail, phone: Phone, address: MapPin };

// Footer contact block modelled on the official letterhead. Approved details always print (and repeat at the foot of every
// printed page); pending details (the office address) appear only in this staff preview and are hidden from print/PDF.
const LetterheadFooter = ({ letterhead }) => (
  <footer className="tc-print-footer border-t border-stone-200 px-6 py-6 sm:px-12 print:px-0 print:py-[6pt]" data-testid="letterhead-footer">
    <div className="flex flex-wrap gap-3 print:grid print:grid-cols-2 print:gap-[5pt]">
      {(letterhead?.verified ?? []).map(([k, v, region, label]) => (
        <span key={v} className={k === "address" ? "contents print:col-span-2 print:block" : "contents"}>
        <Pill icon={ICON[k] ?? Globe} testId={k === "phone" ? `letterhead-phone-${region?.toLowerCase()}` : `letterhead-${k}`}>
          {k === "phone" || k === "address"
            ? <><span className="mr-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-orange-700 print:mr-[4pt] print:text-[6.5pt] print:tracking-[0.08em]">{region}{label ? ` · ${label}` : ""}</span><span className={k === "address" ? "tracking-[0.04em] print:text-[8.5pt]" : "print:text-[8.5pt] print:tracking-[0.06em]"}>{v}</span></>
            : <Domain text={v} />}
        </Pill>
        </span>
      ))}
    </div>
    {(letterhead?.pending ?? []).length > 0 && (
      <div className="mt-4 rounded-xl border border-dashed border-amber-400 bg-amber-50 p-4 print:hidden" data-testid="letterhead-pending">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-900">Pending confirmation · never printed</p>
        <ul className="mt-2 grid gap-2 text-sm text-stone-800 sm:grid-cols-2">
          {letterhead.pending.map(([k, v, label]) => (
            <li key={v} className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
              <span><span className="font-semibold">{v}</span><span className="block text-xs text-stone-600">{label}</span></span>
            </li>
          ))}
        </ul>
      </div>
    )}
    <p className="mt-4 text-xs text-stone-600 print:mt-[5pt] print:text-[7.5pt]">TechCare Technologies · Where innovation meets care. · You take care of business. We take care of the tech.</p>
  </footer>
);

// Reusable branded document shell (capability statements, quotations, proposals, letters, tender covers).
// Layout follows the official letterhead: white sheet, centred official logo, copper rule, contact pills at the foot.
// Print: the table's tfoot spacer repeats on every page and reserves room for the fixed, repeating contact footer.
export const DocumentShell = ({ title, version, status, letterhead, children, testId }) => (
  <article className="tc-print mx-auto max-w-[860px] overflow-hidden rounded-[24px] bg-white text-stone-900 shadow-2xl print:overflow-visible print:rounded-none print:shadow-none" data-testid={testId}>
    <table className="w-full border-collapse">
      <tbody><tr><td className="p-0 align-top">
        <header className="flex justify-center px-6 pb-6 pt-10 sm:px-12 print:px-0 print:pb-[10pt] print:pt-0">
          <Logo variant="light" glow={false} className="h-24 w-auto sm:h-28 print:h-[22mm]" />
        </header>
        <div className="mx-6 h-[3px] rounded-full bg-gradient-to-r from-orange-500 via-orange-800 to-stone-900 sm:mx-12 print:mx-0" aria-hidden="true" />
        <div className="px-6 py-8 sm:px-12 sm:py-10 print:px-0 print:py-[8pt]">
          {status && <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 print:hidden" data-testid="doc-draft-banner">{status}</p>}
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-orange-800 print:mt-[4pt]">{version}</p>
          <h1 className="mt-2 font-display text-3xl font-medium leading-tight tracking-tight sm:text-4xl">{title}</h1>
          {children}
        </div>
      </td></tr></tbody>
      <tfoot className="hidden print:table-footer-group" aria-hidden="true"><tr><td className="p-0"><div className="tc-footer-space" /></td></tr></tfoot>
    </table>
    <LetterheadFooter letterhead={letterhead} />
  </article>
);

const Pending = ({ text }) => (
  <p className="mt-4 rounded-lg border-l-4 border-amber-500 bg-amber-100/70 px-4 py-2 text-sm text-amber-950 print:hidden" data-testid="doc-pending">
    <strong className="font-semibold">Pending confirmation:</strong> {text}
  </p>
);

const DocTable = ({ t }) => (
  <div className="mt-4 overflow-x-auto" data-testid="doc-table">
    <table className="w-full min-w-[480px] border-collapse text-sm">
      <thead><tr className="border-b-2 border-orange-500 text-left">{t.columns.map((c) => <th key={c} className="py-2 pr-3 font-semibold text-stone-900">{c}</th>)}</tr></thead>
      <tbody>{t.rows.map((r, i) => <tr key={i} className="border-b border-stone-200">{r.map((c, j) => <td key={j} className="py-2 pr-3 text-stone-700">{c}</td>)}</tr>)}</tbody>
      {t.totals && <tfoot>{t.totals.map(([k, v]) => (
        <tr key={k}><td colSpan={t.columns.length - 1} className="py-1.5 pr-3 text-right font-semibold text-stone-900">{k}</td><td className="py-1.5 font-semibold text-stone-900">{v}</td></tr>
      ))}</tfoot>}
    </table>
  </div>
);

const DocSection = ({ s }) => (
  <section className="mt-10" data-testid={`doc-section-${s.id}`}>
    <h2 className="border-b border-stone-300 pb-2 font-display text-lg text-stone-900 md:text-xl">{s.heading}</h2>
    {Array.isArray(s.fields) && (
      <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
        {s.fields.map(([k, v]) => <div key={k}><dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-stone-500">{k}</dt><dd className="mt-0.5 text-sm text-stone-900">{v}</dd></div>)}
      </dl>
    )}
    {(s.body ?? []).map((p, i) => <p key={i} className="mt-4 text-[15px] leading-relaxed text-stone-700">{p}</p>)}
    {Array.isArray(s.items) && (
      <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {s.items.map(([t, d]) => (
          <div key={t}><dt className="text-sm font-semibold text-stone-900">{t}</dt><dd className="mt-1 text-sm leading-relaxed text-stone-700">{d}</dd></div>
        ))}
      </dl>
    )}
    {s.table && <DocTable t={s.table} />}
    {s.contact && (
      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-3" data-testid="doc-contact">
        {Object.entries(s.contact).map(([k, v]) => (
          <div key={k}><dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">{k}</dt><dd className="mt-1 text-stone-900">{v}</dd></div>
        ))}
      </dl>
    )}
    {s.pending && <Pending text={s.pending} />}
  </section>
);

export const CompanyDocs = () => {
  const [docId, setDocId] = useState("capability");
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/company-docs/${docId}`).then((r) => { if (alive) setDoc(r.data ?? null); }).catch((err) => toast.error(formatApiError(err)));
    return () => { alive = false; };
  }, [docId]);
  return (
    <div className="mt-6" data-testid="admin-company-docs">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        {DOC_TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setDocId(k)} aria-pressed={docId === k} data-testid={`company-doc-tab-${k}`}
            className={`h-9 rounded-full px-4 text-xs ${docId === k ? "bg-white text-black" : "border border-white/15 text-stone-300"}`}>{l}</button>
        ))}
        <button type="button" onClick={() => window.print()} data-testid="company-doc-print-btn"
          className="ml-auto inline-flex h-9 items-center gap-2 rounded-full border border-white/15 px-4 text-xs text-stone-300 hover:border-orange-500">
          <Printer className="h-3.5 w-3.5" /> Print / save as PDF
        </button>
      </div>
      <p className="mt-3 text-xs text-stone-400 print:hidden">Internal drafts for staff only, not published. The layout follows the official letterhead. {doc?.print_note}</p>
      {doc?.id === docId && (
        <div className="mt-4">
          <DocumentShell title={doc.title} version={doc.version} status={doc.status} letterhead={doc.letterhead} testId={`company-doc-${docId}`}>
            {(doc.sections ?? []).map((s) => <DocSection key={s.id} s={s} />)}
          </DocumentShell>
        </div>
      )}
    </div>
  );
};
